import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { CalendarDays, MapPin, Repeat, Star, Ticket, ArrowUpRight, Music, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";
import { SkeletonGrid, SkeletonEventCard } from "../components/Skeleton";

/**
 * Format an event's start time relative to *today* in Brisbane. Returns
 * "Today 7pm", "Tomorrow 8:30pm", or a full "Thu 27 Jun 7pm" otherwise.
 * The Brisbane date is derived via Intl so users viewing from other timezones
 * still see the venue-local "today".
 */
function formatEventStart(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const brisFmt = new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Brisbane",
    year: "numeric", month: "2-digit", day: "2-digit",
  });
  const eventDay = brisFmt.format(d);
  const todayDay = brisFmt.format(new Date());
  const tomorrowDay = brisFmt.format(new Date(Date.now() + 86400000));
  const time = d.toLocaleString("en-AU", {
    hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Australia/Brisbane",
  })
    .replace(":00", "")            // drop trailing :00
    .replace(/\s?AM/i, "am")       // "7 AM" or "7AM" → "7am"
    .replace(/\s?PM/i, "pm");      // "7 PM" → "7pm"
  if (eventDay === todayDay) return `Today ${time}`;
  if (eventDay === tomorrowDay) return `Tomorrow ${time}`;
  return d.toLocaleString("en-AU", {
    weekday: "short", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit", timeZone: "Australia/Brisbane",
  });
}

export default function Events() {
  const [params, setParams] = useSearchParams();
  const tag = params.get("tag") || "";
  const category = params.get("category") || "";
  const [events, setEvents] = useState(null);

  const setTagFilter = (t) => {
    const p = new URLSearchParams(params);
    if (t && t !== tag) p.set("tag", t); else p.delete("tag");
    setParams(p);
  };

  useEffect(() => {
    const p = new URLSearchParams();
    if (tag) p.set("tag", tag);
    if (category) p.set("category", category);
    api.get(`/events${p.toString() ? `?${p.toString()}` : ""}`).then((r) => setEvents(r.data));
  }, [tag, category]);

  const isLiveMusic = category === "live_music" || tag.toLowerCase() === "live music";
  const headlineEyebrow = isLiveMusic ? "Live Music" : "What's on";
  const headlineTitle = isLiveMusic ? "Live music in Airlie" : "Events in and around Airlie";
  const eventCount = events?.length ?? 0;
  const subline = isLiveMusic
    ? "Gigs, sessions and bands across the Whitsundays"
    : `${eventCount} upcoming event${eventCount === 1 ? "" : "s"}`;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="text-sm tracking-widest text-coastal uppercase flex items-center gap-2">
              {isLiveMusic && <Music className="w-4 h-4" />} {headlineEyebrow}
            </span>
            <h1 className="font-heading text-4xl lg:text-5xl text-navy tracking-tight mt-2" data-testid="events-title">
              {headlineTitle}
            </h1>
            <p className="text-neutral-500 mt-2">
              {isLiveMusic ? `${eventCount} upcoming gig${eventCount === 1 ? "" : "s"}` : subline}
            </p>
          </div>
          {tag || category ? (
            <Link to="/events" className="text-sm text-neutral-500 hover:text-navy underline-offset-4 hover:underline">
              Clear filter →
            </Link>
          ) : null}
        </div>

        {isLiveMusic && (
          <a
            href="https://whitsundaygigguide.com.au"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="live-music-sponsor"
            onClick={() => api.post("/sponsor-clicks", { sponsor: "whitsunday_gig_guide", surface: "web_live_music_banner", target_url: "https://whitsundaygigguide.com.au" }).catch(() => {})}
            className="mb-8 flex items-center gap-4 bg-navy text-cream rounded-2xl p-4 border border-black/5 hover:bg-navy/95 transition-colors"
          >
            <img
              src="/wgg.png"
              alt="Whitsunday Gig Guide logo"
              className="w-14 h-14 rounded-full object-cover flex-shrink-0"
            />
            <div className="flex-1">
              <div className="text-[10px] tracking-widest text-sponsored uppercase font-semibold">
                Sponsored by
              </div>
              <div className="font-heading text-lg mt-0.5">Whitsunday Gig Guide</div>
            </div>
            <ExternalLink className="w-4 h-4 text-cream/70" />
          </a>
        )}

        {/* Tag filter pills — hidden on Live Music (has its own scope) */}
        {!isLiveMusic && (
          <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="event-tag-filters">
            {[
              { slug: "", label: "All" },
              { slug: "kids", label: "Kids" },
              { slug: "family", label: "Family" },
              { slug: "daytime", label: "Daytime" },
              { slug: "party", label: "Party" },
            ].map((p) => {
              const active = (p.slug || "") === tag;
              return (
                <button
                  key={p.slug || "all"}
                  onClick={() => setTagFilter(p.slug)}
                  className={`px-4 py-1.5 rounded-full text-sm transition-colors ${active ? "bg-navy text-white" : "bg-white border border-black/10 text-neutral-700 hover:border-navy"}`}
                  data-testid={`event-tag-${p.slug || "all"}`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events === null ? (
            <>
              <SkeletonEventCard /><SkeletonEventCard /><SkeletonEventCard />
              <SkeletonEventCard /><SkeletonEventCard /><SkeletonEventCard />
            </>
          ) : events.map((e, i) => {
            const linkTo = `/events/${e.id}`;
            return (
              <motion.div
                key={e.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="card-hover rounded-2xl overflow-hidden bg-white border border-black/5 shadow-sm"
                data-testid={`event-${e.id}`}
              >
                <Link to={linkTo} className="block group" data-testid={`event-link-${e.id}`}>
                  {e.image && (
                    <div className="aspect-[16/10] overflow-hidden bg-sand/30 relative">
                      <img src={e.image} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      {e.editors_pick && (
                        <span className="absolute top-3 right-3 bg-sponsored text-navy text-xs px-2.5 py-1 rounded-full flex items-center gap-1 font-semibold shadow" data-testid={`editors-pick-${e.id}`}>
                          <Star className="w-3 h-3 fill-navy" /> Editor&apos;s pick
                        </span>
                      )}
                      {e.is_recurring && (
                        <span className="absolute top-3 left-3 bg-coastal text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1 font-medium" data-testid={`recurring-badge-${e.id}`}>
                          <Repeat className="w-3 h-3" />
                          {e.recurrence === "weekly" ? "Every week" : e.recurrence === "biweekly" ? "Every 2 weeks" : "Monthly"}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-sm text-coastal mb-2">
                      <CalendarDays className="w-4 h-4" />
                      {e.is_recurring && <span className="text-neutral-500">Next:</span>}
                      {formatEventStart(e.start_time)}
                    </div>
                    <h3 className="font-heading text-xl text-navy mb-2 group-hover:underline underline-offset-4 decoration-coastal/40">{e.title}</h3>
                    <p className="text-sm text-neutral-500 line-clamp-3 mb-4">{e.description}</p>
                    <div className="flex items-center justify-between text-xs text-neutral-500">
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.venue_name || e.location}</span>
                      {e.price && <span className="font-medium text-navy">{e.price}</span>}
                    </div>
                  </div>
                </Link>

                {/* Reserve / Book CTA — outside the Link so its click doesn't navigate */}
                {e.booking_url && (
                  <div className="px-6 pb-6 -mt-2">
                    <a
                      href={e.booking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy text-white text-sm font-medium hover:bg-navy/90 transition-colors"
                      data-testid={`reserve-btn-${e.id}`}
                    >
                      <Ticket className="w-4 h-4" /> Reserve <ArrowUpRight className="w-3.5 h-3.5 opacity-70" />
                    </a>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
